/**
 * İzin kataloğu — kategori başlıkları, sıralama, alt gruplar ve kısa etiketler.
 *
 * NEDEN ORTAK DOSYA: bu tablolar iki ekranda birden kullanılıyor — rol şablonu
 * drawer'ı (firm/tabs/storageRoles/RoleTemplatesTab.js) ve kullanıcı izin
 * sekmesi (user/PermissionsTab.js). Ayrı ayrı tutulduklarında sunucuya yeni
 * bir izin eklendiğinde biri güncellenip diğeri unutuluyordu: "sales" ve
 * "factory" kategorileri rol şablonunda Türkçeleşti ama kullanıcı ekranında
 * ham key olarak kaldı (2026-08-26).
 *
 * Sunucu karşılığı: BayinerIOT-Server/src/modules/user/domain/
 * storage-permissions.config.js — yeni izin oraya eklendiğinde etiketi de
 * buraya eklenmeli. Etiket bulunamazsa ekranlar sunucudan gelen açıklamaya
 * düşer, o da yoksa ham key gösterilir.
 */

// eslint-disable-next-line import/prefer-default-export
export const PERMISSION_CATEGORIES = {
  dashboard: "Dashboard",
  alerts: "Uyarılar",
  devices: "Cihazlar",
  dealers: "Bayiler",
  customers: "Müşteriler",
  firms: "Firmalar",
  bills: "Faturalar",
  users: "Kullanıcılar",
  reports: "Raporlar",
  parameters: "Parametreler",
  offers: "Teklifler",
  wallet: "Bakiye",
  scheduler: "Zamanlayıcı",
  pendingDevices: "Onay Bekleyen Cihazlar",
  sales: "Satış",
  factory: "Fabrika",
  programming: "Programlama Paneli",
  development: "Geliştirme",
  tasks: "İş Planlama",
  kurumsal: "Okkalı Kurumsal",
};

export const CATEGORY_ALIAS = {
  products: "parameters",
  // firm.subFirms / firm.consumption yalnız Okkalı Kurumsal müşteri ekranlarını
  // açar (Server customer-dashboard.service.js) — kurumsal.access ile aynı
  // başlıkta durur. Depo kategorisi "firms" ayrı bir önek, buna girmez.
  firm: "kurumsal",
};

export const SUB_GROUP_MAP = {
  devices: {
    "Liste & Genel": ["devices.read", "devices.distribution", "devices.create", "devices.apkRead", "devices.apkUpdate", "devices.hardwareRead", "devices.hardwareUpdate"],
    "Sekmeler": ["devices.info", "devices.movement", "devices.parts", "devices.location", "devices.settings", "devices.maintenance", "devices.stats", "devices.paymentRejections"],
    "Bilgi Kartı Görünümü": ["devices.infoFirm", "devices.infoVersion", "devices.infoGsm", "devices.infoNote", "devices.infoProducts", "devices.developerPassword"],
    "Bilgi Düzenleme": ["devices.editNote", "devices.editName", "devices.editModelName", "devices.editSerial", "devices.editQuotaSettings", "devices.editQuotaDistribution", "devices.employeeOnlyPayment"],
    "Raporlar": ["devices.reports", "devices.reportConsumption", "devices.reportOperation", "devices.reportSettings", "devices.reportQuota", "devices.reportInfo", "devices.lastOperation"],
    "Operasyonlar": ["devices.toggleActivity", "devices.moveToFirm", "devices.moveToService", "devices.moveToPlaymaker", "devices.moveToStorage", "devices.delete"],
    "Kota": ["devices.quota", "devices.productAutoDistributeQuota"],
  },
  firms: {
    "Liste & Genel": ["firms.read", "firms.info", "firms.create", "firms.delete"],
    "Bilgi Düzenleme": ["firms.editName", "firms.editBayserNo", "firms.editDetails", "firms.editActivity", "firms.editAutoReport", "firms.editMainFirm", "firms.editDealerRelation"],
    "Sekmeler": ["firms.devices", "firms.stats", "firms.bills", "firms.productSettings", "firms.belowFirms", "firms.users", "firms.customers", "firms.roleTemplatesRead", "firms.roleTemplatesCreate", "firms.roleTemplatesEdit", "firms.roleTemplatesDelete", "firms.employeeTemplatesRead", "firms.employeeTemplatesCreate", "firms.employeeTemplatesEdit", "firms.employeeTemplatesDelete"],
    "Kullanıcı İşlemleri": ["firms.createUser"],
    "Alt Firma İşlemleri": ["firms.belowFirmsLink", "firms.belowFirmsUnlink", "firms.sendSubFirmQuota"],
    "Çalışan İşlemleri": ["firms.employees", "firms.employeesLink", "firms.employeesUnlink", "firms.employeesEditQuota"],
    "Loglar": ["firms.logConsumption", "firms.logInfo", "firms.logBill", "firms.logSettings", "firms.logOperation", "firms.logQuota"],
    "Ürün Ayarları": ["firms.editProductSettings", "firms.editProductQuotaDistribution", "firms.editProductQuotaWarning"],
    "Kota Operasyonları": ["firms.giveQuota", "firms.takeBackQuota", "firms.distributeQuota"],
    "Özet Kartları": ["firms.billingSummary", "firms.consumptionSummary"],
  },
  // Parametreler = sehir/ilce + urun tanimlari (bkz. CATEGORY_ALIAS).
  // Her tanim kendi alt grubunda: goruntule/olustur/duzenle/sil dortlusu
  // yan yana dururken hangi dortlunun neye ait oldugu karisiyordu.
  parameters: {
    "Şehir": ["parameters.cityRead", "parameters.cityCreate", "parameters.cityEdit", "parameters.cityDelete"],
    "İlçe": ["parameters.townRead", "parameters.townCreate", "parameters.townEdit", "parameters.townDelete"],
    "Ürün Tipleri": ["products.typeRead", "products.typeCreate", "products.typeEdit", "products.typeDelete"],
    "Ürün Bilgileri": ["products.infoRead", "products.infoCreate", "products.infoEdit", "products.infoDelete"],
  },
  // Fabrika izinleri iki FARKLI yuzeye ait: panel sayfalari ve tablet
  // eylemleri. Ayrimi gormek kritik, cunku yalnizca "Tablet Eylemleri"
  // secili bir sablondaki kullanici PANELE GIREMEZ (bkz. erisim ozeti).
  factory: {
    "Panel — Görüntüleme": ["factory.overview.read", "factory.parts.read", "factory.machines.read", "factory.zones.read", "factory.operation.stats", "factory.audit.read"],
    "Panel — Yönetim": ["factory.parts.manage", "factory.zones.manage", "factory.catalog.manage", "factory.operation.cancel.admin", "factory.documents.manage"],
    "Tablet Eylemleri (kart okutma)": ["factory.action.uretim", "factory.action.yerlestirme", "factory.action.ariza-tespit-sokum", "factory.action.temizlik", "factory.action.paketleme"],
    // Belge grupları da TABLET izinleridir — panelde sayfa AÇMAZ. Yükleme/
    // silme yetkisi ayrı bir panel iznidir ("factory.documents.manage",
    // Panel — Yönetim altında). Yeni grup eklerken: burası + sunucudaki
    // storage-permissions.config.js birlikte güncellenir.
    "Belgeler (kart okutma)": ["factory.docs.teknik-dosyalar"],
  },
  // Programlama Paneli (Pi istasyonu) — gruplar panelin KENDİ menüsüyle aynı
  // sırada: kullanım → ekran → kart → ayarlar. Her bölümün bir TEPE izni var
  // ("... (bölüme erişim)"); tepe izin yoksa bölüm hiç açılmaz, alt izinler
  // yalnızca listede neyin seçilebileceğini belirler.
  programming: {
    "Kullanım": ["programming.use", "programming.read"],
    "Ekran Programlama": ["programming.screen.machine", "programming.screen.factory", "programming.screen.kiosk", "programming.screen.test"],
    "Ekranda Diğer İşlemler": ["programming.screen.lock", "programming.screen.cleanup", "programming.screen.settings", "programming.screen.appRemove", "programming.screen.launcherRemove", "programming.screen.ntp", "programming.screen.reset"],
    "Kart Programlama": ["programming.card.cayset", "programming.card.cup2", "programming.card.cup4", "programming.card.cup6", "programming.card.hanger", "programming.card.grinder", "programming.card.erase"],
    "Yazılım Paketleri": ["programming.package.apk", "programming.package.screen", "programming.package.hardware"],
  },
  reports: {
    "Firma Raporları": ["reports.firmBalance", "reports.firmNew", "reports.firmBilling", "reports.firmLocation", "reports.firmMonthlyConsumption", "reports.firmDailyConsumption", "reports.firmTotalConsumption", "reports.firmDailyPerformance", "reports.firmPerformance", "reports.firmRangePerformance", "reports.firmsPerformance"],
    "Cihaz Raporları": ["reports.deviceConnection", "reports.deviceGeneral", "reports.deviceNew", "reports.deviceStatus", "reports.deviceServiceStatus", "reports.devicePerformance", "reports.devicesAtDate"],
    "Gönderim": ["reports.send"],
  },
};

export const CATEGORY_ORDER = [
  "dashboard", "alerts", "devices", "dealers", "customers",
  "firms", "bills", "sales", "users", "reports",
  "parameters", "offers", "wallet", "scheduler", "pendingDevices",
  "factory", "programming", "tasks", "development", "kurumsal",
];

export const PERMISSION_LABELS = {
  // Dashboard
  "dashboard.read": "Dashboard Tüketim Kartları",
  "dashboard.graph": "Tüketim Grafiği",
  "dashboard.deviceStatus": "Cihaz Durum Kartları",
  "dashboard.firmStatus": "Firma Durum Kartları",
  "dashboard.map": "Cihaz Haritası",
  "dashboard.billDetails": "Dashboard Fatura Detayları",
  "dashboard.quota": "Bayi Kotası Kartı",
  // Uyarılar sayfası (#229)
  "alerts.read": "Uyarılar Sayfası",
  "alerts.devices": "Cihaz Uyarıları",
  "alerts.firms": "Firma Uyarıları (Kritik Kota, Düşük Performans)",
  "alerts.orders": "Sipariş Vermeyen Firmalar",
  // Cihaz
  "devices.read": "Cihaz Listesi Görüntüleme",
  "devices.distribution": "Cihaz Dağılım Matrisi",
  "devices.create": "Yeni Cihaz Oluşturma",
  "devices.apkRead": "APK Listesini Görüntüleme",
  "devices.apkUpdate": "APK Yükleme / Güncelleme / Silme",
  "devices.hardwareRead": "Hardware Listesini Görüntüleme",
  "devices.hardwareUpdate": "Hardware Yükleme / Silme",
  "devices.info": "Cihaz Bilgi Sekmesi",
  "devices.movement": "Cihaz Hareket Sekmesi",
  "devices.infoFirm": "Firma Bilgileri Kartı",
  "devices.infoVersion": "Versiyon Bilgileri Kartı",
  "devices.infoGsm": "GSM Bilgileri Kartı",
  "devices.infoNote": "Notlar Kartı",
  "devices.infoProducts": "Ürün Bilgileri Kartları",
  "devices.developerPassword": "Developer Parolası Görüntüleme",
  "devices.parts": "Cihaz Parçalar Sekmesi",
  "devices.location": "Cihaz Konum Sekmesi",
  "devices.settings": "Cihaz Ayarlar Sekmesi",
  "devices.maintenance": "Cihaz Bakım Sekmesi (Canlı Sensör İzleme, Hassasiyet)",
  "devices.stats": "Cihaz İstatistik Sekmesi",
  "devices.paymentRejections": "Cihaz Ödeme Retleri Sekmesi",
  "devices.editNote": "Cihaz Notu Düzenleme",
  "devices.editName": "Cihaz Özel Adı Değiştirme",
  "devices.editModelName": "Cihaz Model İsmi Değiştirme",
  "devices.editSerial": "Cihaz Seri No Değiştirme",
  "devices.editQuotaSettings": "Ürün Kota Ayarları",
  "devices.editQuotaDistribution": "Kota Dağıtım Aktifliği",
  "devices.employeeOnlyPayment": "Çalışan Ödeme Makinesi",
  "devices.reports": "Cihaz Raporlar Sekmesi",
  "devices.reportConsumption": "Cihaz Tüketim Logları",
  "devices.reportOperation": "Cihaz İşlem Logları",
  "devices.reportSettings": "Cihaz Ayar Değişiklik Logları",
  "devices.reportQuota": "Cihaz Kota Logları (Yükleme + Değişim)",
  "devices.reportInfo": "Cihaz Bilgi Değişiklik Logları",
  "devices.lastOperation": "Cihaz Son İşlem",
  "devices.toggleActivity": "Cihaz Kota Almaya Aç/Kapa",
  "devices.moveToFirm": "Cihazı Firmaya Ver",
  "devices.moveToService": "Cihazı Servise Al",
  "devices.moveToPlaymaker": "Cihazı Plasiyere Ver",
  "devices.moveToStorage": "Cihazı Depoya Al",
  "devices.delete": "Cihaz Silme",
  "devices.quota": "Manuel Kota Yükleme",
  "devices.productAutoDistributeQuota": "Kota Dağıtım Aktifliği",
  // Bayiler
  "dealers.read": "Bayi Listesi Görüntüleme (Bayilerim)",
  "dealers.info": "Bayi Bilgi Sekmesi",
  "dealers.billingSummary": "Bayi Son Fatura Özet Kartı",
  "dealers.consumptionSummary": "Bayi Kota Tüketimi Özet Kartı",
  "dealers.editName": "Bayi Adı Değiştirme",
  "dealers.editDetails": "Bayi İletişim/Vergi/Not Düzenleme",
  "dealers.editActivity": "Bayi Aktiflik Aç/Kapat",
  "dealers.editAutoReport": "Bayi Otomatik Rapor Ayarları Düzenleme",
  "dealers.devices": "Bayi Cihazlar Sekmesi",
  "dealers.stats": "Bayi Performans Sekmesi",
  "dealers.productSettings": "Bayi Ürün Ayarları Sekmesi",
  "dealers.editProductSettings": "Bayi Ürün Ayarları Düzenleme",
  "dealers.editProductQuotaDistribution": "Bayi Ürün Kota Almaya Açık/Kapalı",
  "dealers.editProductQuotaWarning": "Bayi Ürün Uyarı Kotası",
  "dealers.belowFirms": "Alt Bayiler Sekmesi",
  "dealers.belowFirmsLink": "Alt Bayi Ekleme",
  "dealers.belowFirmsUnlink": "Alt Bayi Çıkarma",
  "dealers.sendSubFirmQuota": "Alt Bayiye Kota Gönderme",
  "dealers.customers": "Bayinin Müşteri Firmaları Sekmesi",
  "dealers.users": "Bayi Kullanıcıları Sekmesi",
  // Müşteriler
  "customers.read": "Müşteri Listesi Görüntüleme (Müşterilerim)",
  "customers.info": "Müşteri Bilgi Sekmesi",
  "customers.billingSummary": "Müşteri Son Fatura Özet Kartı",
  "customers.consumptionSummary": "Müşteri Kota Tüketimi Özet Kartı",
  "customers.editName": "Müşteri Adı Değiştirme",
  "customers.editDetails": "Müşteri İletişim/Vergi/Not Düzenleme",
  "customers.editActivity": "Müşteri Aktiflik Aç/Kapat",
  "customers.editAutoReport": "Müşteri Otomatik Rapor Ayarları Düzenleme",
  "customers.devices": "Müşteri Cihazlar Sekmesi",
  "customers.stats": "Müşteri Performans Sekmesi",
  "customers.productSettings": "Müşteri Ürün Ayarları Sekmesi",
  "customers.editProductSettings": "Müşteri Ürün Ayarları Düzenleme",
  "customers.editProductQuotaDistribution": "Müşteri Ürün Kota Almaya Açık/Kapalı",
  "customers.editProductQuotaWarning": "Müşteri Ürün Uyarı Kotası",
  "customers.sendQuota": "Müşteriye Kota Ver / Al",
  "customers.users": "Müşteri Kullanıcıları Sekmesi",
  "customers.employees": "Müşteri Çalışanları Sekmesi",
  "customers.employeesLink": "Müşteri Çalışanı Ekleme",
  "customers.employeesUnlink": "Müşteri Çalışanı Çıkarma",
  "customers.employeesEditQuota": "Müşteri Çalışan Kota Düzenleme",
  // Firma
  "firms.read": "Firma Listesi Görüntüleme",
  "firms.info": "Firma Bilgi Sekmesi",
  "firms.create": "Yeni Firma Oluşturma",
  "firms.delete": "Firma Silme",
  "firms.editName": "Firma Adı Değiştirme",
  "firms.editBayserNo": "Firma Bayser Numarası Değiştirme",
  "firms.editDetails": "Firma Detay Düzenleme",
  "firms.editActivity": "Firma Aktiflik Aç/Kapat",
  "firms.editAutoReport": "Firma Otomatik Rapor Ayarları Düzenleme",
  "firms.editMainFirm": "Ana Firma Değiştirme",
  "firms.editDealerRelation": "Bayi İlişkisi Ekleme/Kaldırma",
  "firms.devices": "Firma Cihazlar Sekmesi",
  "firms.stats": "Firma Performans Sekmesi",
  "firms.bills": "Firma Faturalar Sekmesi",
  "firms.productSettings": "Firma Ürün Ayarları",
  "firms.belowFirms": "Alt Firmalar Sekmesi",
  "firms.belowFirmsLink": "Alt Firma Ekleme",
  "firms.belowFirmsUnlink": "Alt Firma Çıkarma",
  "firms.sendSubFirmQuota": "Alt Firmaya Kota Gönderme",
  "firms.users": "Firma Kullanıcıları Sekmesi",
  "firms.createUser": "Firma Detayından Kullanıcı Oluşturma",
  "firms.customers": "Müşteri Firmaları Sekmesi",
  "firms.employees": "Çalışanlar Sekmesi",
  "firms.employeesLink": "Çalışan Ekleme",
  "firms.employeesUnlink": "Çalışan Çıkarma",
  "firms.employeesEditQuota": "Çalışan Kota Düzenleme",
  "firms.logConsumption": "Firma Tüketim Logları",
  "firms.logInfo": "Firma Bilgi Değişikliği Logları",
  "firms.logBill": "Firma Fatura Hareketi Logları",
  "firms.logSettings": "Firma Ayar Değişikliği Logları",
  "firms.logOperation": "Firma Operasyon Logları",
  "firms.logQuota": "Firma Kota Değişikliği Logları",
  "firms.roleTemplatesRead": "Kullanıcı Şablonları Görüntüleme",
  "firms.roleTemplatesCreate": "Kullanıcı Şablonu Oluşturma",
  "firms.roleTemplatesEdit": "Kullanıcı Şablonu Düzenleme",
  "firms.roleTemplatesDelete": "Kullanıcı Şablonu Silme",
  "firms.employeeTemplatesRead": "Çalışan Şablonları Görüntüleme",
  "firms.employeeTemplatesCreate": "Çalışan Şablonu Oluşturma",
  "firms.employeeTemplatesEdit": "Çalışan Şablonu Düzenleme",
  "firms.employeeTemplatesDelete": "Çalışan Şablonu Silme",
  "firms.editProductSettings": "Ürün Ayarları Düzenleme",
  "firms.editProductQuotaDistribution": "Ürün Kota Dağıtım Ayarı",
  "firms.editProductQuotaWarning": "Ürün Uyarı Kotası",
  "firms.billingSummary": "Firma Son Fatura Özet Kartı",
  "firms.consumptionSummary": "Firma Kota Tüketimi Özet Kartı",
  "firms.giveQuota": "Bedelsiz Kota Verme",
  "firms.takeBackQuota": "Bedelsiz Kota İade",
  "firms.distributeQuota": "Kota Dağıtma",
  // Fatura
  "bills.read": "Faturaları Görüntüleme",
  "bills.create": "Fatura Oluşturma",
  "bills.controll": "Fatura Kontrol",
  "bills.delete": "Fatura Silme",
  "bills.deleteWithoutQuota": "Fatura Silerken Kota Etkisini Atlama",
  // Kullanıcı
  "users.read": "Kullanıcı Listesi Görüntüleme",
  "users.detail": "Kullanıcı Detay Sayfası",
  "users.createStorage": "Depo Kullanıcısı Oluşturma",
  "users.createDealer": "Bayi Kullanıcısı Oluşturma",
  "users.createCustomer": "Müşteri Kullanıcısı Oluşturma",
  "users.createEnduser": "Son Kullanıcı Oluşturma",
  "users.createMachineworker": "Makine İşçisi Oluşturma",
  "users.edit": "Kullanıcı Bilgi Düzenleme",
  "users.editAutoReport": "Kullanıcı Otomatik Rapor Ayarları Düzenleme",
  "users.changeFirm": "Kullanıcı Firma Değiştirme",
  "users.changeStatus": "Kullanıcı Aktiflik Değiştirme",
  "users.resendInvitation": "Davet Tekrar Gönderme",
  "users.viewPermissions": "Yetkiler Tabı Görüntüleme",
  "users.activity": "Kullanıcı Son Hareketleri",
  "users.walletRead": "Son Kullanıcı Cüzdanı Görüntüleme",
  "users.okkaliraGrant": "Son Kullanıcıya Okkalira Yükleme",
  "users.editPermissions": "Kullanıcı Yetki Düzenleme",
  "users.assignDevices": "Makine Görevlisine Cihaz Atama",
  "users.delete": "Kullanıcı Silme",
  // Rapor
  "reports.firmBalance": "Müşteri Bakiye Seviyeleri Raporu",
  "reports.firmNew": "Müşteri Eklenenler Raporu",
  "reports.firmBilling": "Yüklenen Faturalar Raporu",
  "reports.firmLocation": "Müşteri Listesi Konum Merkezli Raporu",
  "reports.firmMonthlyConsumption": "Firma Aylık Tüketim Raporu",
  "reports.firmDailyConsumption": "Firma Günlük Tüketim Raporu",
  "reports.firmTotalConsumption": "Firma Toplam Tüketim Raporu",
  "reports.firmDailyPerformance": "Müşteri Performans Raporu — Gün",
  "reports.firmPerformance": "Müşteri Performans Raporu — Ay",
  "reports.firmRangePerformance": "Müşteri Performans Raporu — Tarih Aralığı",
  "reports.firmsPerformance": "Müşteri Performans Raporu — Yıl (çoklu müşteri)",
  "reports.deviceConnection": "Cihaz Bağlantı Bilgileri Raporu",
  "reports.deviceGeneral": "Cihaz Genel Bilgileri Raporu",
  "reports.deviceNew": "Yeni Cihaz Raporu",
  "reports.deviceStatus": "Cihaz Hareket Raporu",
  "reports.deviceServiceStatus": "Cihaz Servis Hareketleri Raporu",
  "reports.devicePerformance": "Cihaz Performans Raporu",
  "reports.devicesAtDate": "Tarihteki Makineler ve Makine Geçmişi Raporu",
  // Ürün
  "dashboard.deviceConsumption": "Dashboard Cihaz Tüketim Detayı",
  "reports.send": "Manuel Rapor Gönderme (e-posta)",

  // Satış
  "sales.read": "Satış Dashboard'u",

  // Fabrika — panel yetkileri
  "factory.overview.read": "Fabrika Genel Bakış",
  "factory.parts.read": "Parça Kataloğu ve Envanteri",
  "factory.parts.manage": "Parça Ünitesi Silme",
  "factory.machines.read": "Üretim Kayıtları (Makineler)",
  "factory.zones.read": "Bölgeler ve Tabletler",
  "factory.zones.manage": "Bölge Yönetimi",
  "factory.catalog.manage": "Fabrika Katalog Yönetimi",
  "factory.operation.stats": "Fabrika İstatistikleri",
  "factory.operation.cancel.admin": "İşlem Geç İptali",
  "factory.audit.read": "Fabrika Denetim Kayıtları",
  "development.read": "Geliştirme Panosu Görüntüleme",
  "tasks.access": "Erişim",
  "tasks.admin": "İş Planlama Yönetimi",
  "factory.documents.manage": "Fabrika Belge Yönetimi",


  // Fabrika — tablet eylemleri (kart okutunca yapilabilecekler)
  "factory.action.uretim": "Fabrika Eylemi: Üretim",
  "factory.action.yerlestirme": "Fabrika Eylemi: Yerleştirme",
  "factory.action.ariza-tespit-sokum": "Fabrika Eylemi: Arıza Tespit Söküm",
  "factory.action.temizlik": "Fabrika Eylemi: Temizlik",
  "factory.action.paketleme": "Fabrika Eylemi: Paketleme",

  // Fabrika — belge gruplari (tablette kart okutunca gorunen PDF'ler).
  // Ad belgenin KONUSUNA gore verilir, kimin okuyacagina gore DEGIL.
  "factory.docs.teknik-dosyalar": "Belge Grubu: Teknik Dosyalar",

  // Programlama Paneli (Okkalı Servis paneli — Pi istasyonu)
  "programming.read": "Programlama İşlemleri (faaliyet logu)",
  "programming.use": "Programlama Paneline Giriş",
  "programming.screen.machine": "Makine Ekranı Program Atma",
  "programming.screen.factory": "Fabrika Ekranı Program Atma",
  "programming.screen.kiosk": "Programlama Ekranı Program Atma",
  "programming.screen.test": "Makine Ekranı Test Sürümü Yükleme",
  "programming.screen.lock": "Geliştirici Modunu Kapatma",
  "programming.screen.cleanup": "Ekranda Yer Açma",
  "programming.screen.settings": "Ekranda Ayarlar Menüsü",
  "programming.screen.appRemove": "Ekrandan Uygulamayı Silme",
  "programming.screen.launcherRemove": "Ekrandan Ana Ekranı Silme",
  "programming.screen.ntp": "Ekranda Saat Sunucusu Ayarlama",
  "programming.screen.reset": "DWIN Ekran Sıfırlama",
  "programming.card.cayset": "Kart: Çayset Anakart",
  "programming.card.cup2": "Kart: Fincanda 2'li Anakart",
  "programming.card.cup4": "Kart: Fincanda 4'lü Anakart",
  "programming.card.cup6": "Kart: Fincanda 6'lı Anakart",
  "programming.card.hanger": "Kart: Fincanda Askı",
  "programming.card.grinder": "Kart: Değirmen",
  "programming.package.apk": "Paket: Tablet Uygulaması (APK)",
  "programming.package.screen": "Paket: Ekran Yazılımı",
  "programming.package.hardware": "Paket: Kart Yazılımları",
  "programming.card.erase": "Kart Temizleme (chip-erase)",

  "products.typeRead": "Ürün Tipi Görüntüleme",
  "products.typeCreate": "Ürün Tipi Oluşturma",
  "products.typeEdit": "Ürün Tipi Düzenleme",
  "products.typeDelete": "Ürün Tipi Silme",
  "products.infoRead": "Ürün Bilgisi Görüntüleme",
  "products.infoCreate": "Ürün Bilgisi Oluşturma",
  "products.infoEdit": "Ürün Bilgisi Düzenleme",
  "products.infoDelete": "Ürün Bilgisi Silme",
  // Parametreler
  "parameters.cityRead": "Şehir Görüntüleme",
  "parameters.cityCreate": "Şehir Oluşturma",
  "parameters.cityEdit": "Şehir Düzenleme",
  "parameters.cityDelete": "Şehir Silme",
  "parameters.townRead": "İlçe Görüntüleme",
  "parameters.townCreate": "İlçe Oluşturma",
  "parameters.townEdit": "İlçe Düzenleme",
  "parameters.townDelete": "İlçe Silme",
  // Teklif
  "offers.create": "Teklif Oluşturma",
  // Bakiye
  "wallet.topup": "Bakiye Yükleme",
  "wallet.okkaliraRate": "Okkalira Kuru",
  // Zamanlayıcı
  "scheduler.read": "Zamanlayıcı Görüntüleme",
  // Onay Bekleyen Cihazlar
  "pendingDevices.read": "Liste Görüntüleme",
  "pendingDevices.manage": "Onaylama / Reddetme / Atama / Ekran Değişimi",
  // Okkalı Kurumsal (müşteri) — Server customer-permissions.config.js ile aynı metin
  "kurumsal.access": "Okkalı Kurumsal mobil uygulamasına giriş",
  "firm.subFirms": "Alt firmaları ve kırılımını görüntüleme",
  "firm.consumption": "Tüketim ve kota verilerini görüntüleme",
  // Firma detayı (#320) — Kurumsal'da salt-okunur sekmeler
  "firm.info": "Firma bilgilerini görüntüleme",
  "firm.stats": "Firma performansını görüntüleme",
  "firm.productSettings": "Firma ürün ayarlarını görüntüleme",
  "firm.users": "Firma kullanıcılarını görüntüleme",
};

/** Kategori gösterim sırası — listede olmayanlar sona eklenir. */
// İş Planlama 1.5 — kaldırılan izinler tasks.admin'de birleşti (Server
// permission-migration.js ile aynı). Eski şablon açılırken kutu doğru görünsün.
const LEGACY_PERMISSION_MAP = { "tasks.viewAll": "tasks.admin", "tasks.editAll": "tasks.admin" };
export const migrateLegacyPermissions = (perms = []) => [
  ...new Set(perms.map((p) => LEGACY_PERMISSION_MAP[p] || p)),
];

export const orderCategories = (entries) =>
  [...entries].sort(([a], [b]) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

/** İzin key'inden gösterim kategorisi (takma adlar uygulanır). */
export const categoryOf = (key) => {
  const raw = String(key).split(".")[0];
  return CATEGORY_ALIAS[raw] || raw;
};

export const categoryLabel = (cat) => PERMISSION_CATEGORIES[cat] || cat;

/**
 * Kategori içindeki izinleri alt gruplara ayırır.
 * @param {string} cat
 * @param {Array<{key:string}>} perms
 * @returns {Array<{label:string, items:Array}>|null} tanımlı grup yoksa null
 */
export const getSubGroups = (cat, perms) => {
  const subGroupDef = SUB_GROUP_MAP[cat];
  if (!subGroupDef) return null;
  const result = [];
  for (const [label, keys] of Object.entries(subGroupDef)) {
    const items = keys.map((k) => perms.find((p) => p.key === k)).filter(Boolean);
    if (items.length > 0) result.push({ label, items });
  }
  const allMappedKeys = Object.values(subGroupDef).flat();
  const unmapped = perms.filter((p) => !allMappedKeys.includes(p.key));
  if (unmapped.length > 0) result.push({ label: "Diğer", items: unmapped });
  return result;
};
